import React from "react";
import PropTypes from "prop-types";
import clsx from "clsx";
import {makeStyles} from "@material-ui/core/styles";
import ButtonBase from "@material-ui/core/ButtonBase";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";

const SCROLL_AMOUNT = 400;
const ANIMATION_DURATION = 500;

const useStyles = makeStyles((theme) => ({
  wrapper: {
    display: 'flex',
    alignItems: 'stretch',
  },
  container: {
    overflowX: 'auto',
    scrollbarWidth: 'none',
    WebkitOverflowScrolling: 'touch',
    '&::-webkit-scrollbar': {display: 'none'},
    flex: 1,
    minWidth: 0,
  },
  content: {
    display: 'flex',
    flexWrap: 'nowrap',
    gap: '12px',
    padding: '6px 0',
  },
  arrowBtn: {
    width: 25,
    flexShrink: 0,
    zIndex: 1,
    backgroundColor: '#fff',
    boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.15), 0 2px 6px 2px rgba(0, 0, 0, 0.10)',
    color: theme.palette.primary.main,
    transition: 'width 0.2s ease, opacity 0.2s ease',
    '&:hover': {backgroundColor: '#f5f5f5'},
    '&$arrowHidden': {
      width: 0,
      opacity: 0,
      overflow: 'hidden',
      margin: 0,
      boxShadow: 'none',
    },
  },
  arrowHidden: {},
  arrowLeft: {marginRight: 8},
  arrowRight: {marginLeft: 8},
}));

function easeInOutSin(t) {
  return (1 + Math.sin(Math.PI * t - Math.PI / 2)) / 2;
}

// rAF-based scroll animation — adapts to container resize mid-animation (unlike native smooth scroll)
function animateScroll(element, to) {
  const from = element.scrollLeft;
  if (from === to) return;

  let start = null;
  const step = (timestamp) => {
    if (start === null) start = timestamp;
    const progress = Math.min(1, (timestamp - start) / ANIMATION_DURATION);
    element.scrollLeft = from + (to - from) * easeInOutSin(progress);
    if (progress < 1) requestAnimationFrame(step);
  };
  requestAnimationFrame(step);
}

/**
 * Horizontal scrollable container with arrow buttons that appear when content overflows.
 */
const ScrollableContainer = React.memo(({children, className}) => {
  const classes = useStyles();
  const scrollRef = React.useRef(null);
  const [showLeft, setShowLeft] = React.useState(false);
  const [showRight, setShowRight] = React.useState(false);

  const updateArrows = React.useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const {scrollLeft, scrollWidth, clientWidth} = el;
    setShowLeft(scrollLeft > 1);
    setShowRight(scrollLeft + clientWidth < scrollWidth - 1);
  }, []);

  React.useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    updateArrows();
    const observer = new ResizeObserver(updateArrows);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const scroll = React.useCallback((direction) => {
    const el = scrollRef.current;
    if (!el) return;
    const delta = direction === 'left' ? -SCROLL_AMOUNT : SCROLL_AMOUNT;
    animateScroll(el, el.scrollLeft + delta);
  }, []);

  return (
    <div className={clsx(classes.wrapper, className)}>
      <ButtonBase
        className={clsx(classes.arrowBtn, classes.arrowLeft, !showLeft && classes.arrowHidden)}
        onClick={() => scroll('left')}
        disabled={!showLeft}
        tabIndex={showLeft ? 0 : -1}
        aria-label="Scroll left"
      >
        <ChevronLeftIcon />
      </ButtonBase>
      <div ref={scrollRef} className={classes.container} onScroll={updateArrows}>
        <div className={classes.content}>
          {children}
        </div>
      </div>
      <ButtonBase
        className={clsx(classes.arrowBtn, classes.arrowRight, !showRight && classes.arrowHidden)}
        onClick={() => scroll('right')}
        disabled={!showRight}
        tabIndex={showRight ? 0 : -1}
        aria-label="Scroll right"
      >
        <ChevronRightIcon />
      </ButtonBase>
    </div>
  );
});

ScrollableContainer.displayName = 'ScrollableContainer';

ScrollableContainer.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
};

export default ScrollableContainer;
